import { OVERLAY_STALL_MS, PERSON_DETECTION_ENABLED } from "./config";
import { FrameResult, privacyWorkerClient } from "./privacyWorkerClient";

/**
 * Main-thread facade for the shared privacy workers.
 *
 * The visible canvas is transferred once; JPEG decode, ROI scanning, optional
 * blur compositing and optional ONNX inference all happen off-thread.
 * VideoQuic shows raw video until a cyan ROI is confirmed (or detection draws
 * boxes on a raw canvas). CSS blur is only a fallback after ROI was confirmed
 * and the overlay stalls or errors.
 */
export class PrivacyProcessor {
  readonly uavId: string;
  private streamId: string;
  private disposed = false;
  private peek = false;
  private latestFrameId = 0;
  private lastSentAt = 0;
  private workerBusy = false;
  private pendingBlob: Blob | null = null;
  private overlayVisible = false;
  private blurActive = false;
  private hasBoxes = false;
  /**
   * True while the worker reports a current cyan ROI lock. Cleared when a
   * processed frame has no circle so CSS fallback cannot stick after the ring
   * is lost. Stall/error (no frame result) leave this unchanged.
   */
  private roiConfirmed = false;
  private onOverlayChange: ((visible: boolean) => void) | null = null;
  private onBlurActiveChange: ((active: boolean) => void) | null = null;
  private onRoiConfirmedChange: ((confirmed: boolean) => void) | null = null;
  private onFrameSize: ((width: number, height: number) => void) | null = null;
  private frameWidth = 0;
  private frameHeight = 0;

  constructor(
    uavId: string,
    canvas: HTMLCanvasElement,
    options: {
      blurEnabled?: boolean;
      personDetectionEnabled?: boolean;
    } = {}
  ) {
    this.uavId = uavId;
    const blurEnabled = options.blurEnabled !== false;
    const personDetectionEnabled =
      options.personDetectionEnabled !== false && PERSON_DETECTION_ENABLED;

    if (
      typeof Worker === "undefined" ||
      typeof OffscreenCanvas === "undefined" ||
      typeof canvas.transferControlToOffscreen !== "function"
    ) {
      throw new Error("Privacy worker requires OffscreenCanvas support");
    }

    const offscreen = canvas.transferControlToOffscreen();
    this.streamId = privacyWorkerClient.register(
      uavId,
      offscreen,
      { blurEnabled, personDetectionEnabled },
      {
        uavId,
        onFrame: (result: FrameResult) => {
          this.workerBusy = false;
          this.noteFrameSize(result.sourceWidth, result.sourceHeight);
          this.applyFrameResult(result);
          if (!this.disposed && !this.peek) {
            this.flushPendingFrame();
          }
        },
        onError: () => {
          this.workerBusy = false;
          this.pendingBlob = null;
          // Only fail closed (CSS blur) for feeds that already had an ROI.
          // No-ring feeds stay raw.
          this.hideOverlay();
          this.setBlurActive(false);
        },
      }
    );
  }

  setOverlayChangeListener(listener: (visible: boolean) => void): void {
    this.onOverlayChange = listener;
  }

  /**
   * Fired when ROI blur is actively painted. VideoQuic uses this for CSS
   * fallback and geometry (scale) — not for overlay visibility (boxes alone
   * can keep the overlay up without blur).
   */
  setBlurActiveListener(listener: (active: boolean) => void): void {
    this.onBlurActiveChange = listener;
    listener(this.blurActive);
  }

  /** True while a cyan ROI lock is currently confirmed. */
  hasConfirmedRoi(): boolean {
    return this.roiConfirmed;
  }

  setRoiConfirmedListener(listener: (confirmed: boolean) => void): void {
    this.onRoiConfirmedChange = listener;
    listener(this.roiConfirmed);
  }

  isBlurActive(): boolean {
    return this.blurActive;
  }

  /**
   * Size of the box the canvas is painted into. The worker needs this to
   * express the blur radius in display pixels when ROI blur is applied.
   */
  setContainerSize(containerWidth: number, containerHeight: number): void {
    if (!this.disposed) {
      privacyWorkerClient.setContainerSize(
        this.streamId,
        containerWidth,
        containerHeight
      );
    }
  }

  setBlurEnabled(enabled: boolean): void {
    if (!this.disposed) {
      privacyWorkerClient.setBlurEnabled(this.streamId, enabled);
      if (!enabled) {
        this.setRoiConfirmed(false);
        this.setBlurActive(false);
      }
    }
  }

  setPersonDetectionEnabled(enabled: boolean): void {
    if (!this.disposed) {
      privacyWorkerClient.setPersonDetectionEnabled(this.streamId, enabled);
    }
  }

  /** Notified when the source frame size is first seen or changes. */
  setFrameSizeListener(
    listener: (width: number, height: number) => void
  ): void {
    this.onFrameSize = listener;
    if (this.frameWidth > 0) {
      listener(this.frameWidth, this.frameHeight);
    }
  }

  private noteFrameSize(width: number, height: number): void {
    if (
      width <= 0 ||
      height <= 0 ||
      (width === this.frameWidth && height === this.frameHeight)
    ) {
      return;
    }
    this.frameWidth = width;
    this.frameHeight = height;
    if (!this.disposed) {
      this.onFrameSize?.(width, height);
    }
  }

  private applyFrameResult(result: FrameResult): void {
    if (this.disposed || this.peek) {
      return;
    }
    // ROI confirmation tracks the *current* lock, not a sticky lifetime flag.
    // If the ring is gone, clear it so CSS fallback does not leave a permanent
    // full-frame blur on feeds that lost (or never held) a valid cyan ROI.
    // Stall/error paths hide the overlay without a frame result and keep the
    // prior confirmation — that is the only intended CSS-fallback case.
    this.setRoiConfirmed(result.circle !== null);
    this.setBlurActive(result.blurActive);
    this.hasBoxes = result.hasBoxes;
    if (result.blurActive || result.hasBoxes) {
      this.showOverlay();
    } else {
      this.hideOverlay();
    }
  }

  private setRoiConfirmed(confirmed: boolean): void {
    if (this.roiConfirmed === confirmed) {
      return;
    }
    this.roiConfirmed = confirmed;
    this.onRoiConfirmedChange?.(confirmed);
  }

  private setBlurActive(active: boolean): void {
    if (this.blurActive === active) {
      return;
    }
    this.blurActive = active;
    this.onBlurActiveChange?.(active);
  }

  isPeeking(): boolean {
    return this.peek;
  }

  togglePeek(): boolean {
    this.setPeek(!this.peek);
    return this.peek;
  }

  setPeek(peek: boolean): void {
    this.peek = peek;
    this.workerBusy = false;
    this.pendingBlob = null;
    privacyWorkerClient.setPeek(this.streamId, peek);
    if (peek) {
      this.hideOverlay();
      this.setBlurActive(false);
    }
  }

  isCircuitOpen(): boolean {
    return false;
  }

  enqueueFrame(blob: Blob): void {
    if (!this.disposed && !this.peek) {
      if (
        this.workerBusy &&
        this.lastSentAt > 0 &&
        Date.now() - this.lastSentAt > OVERLAY_STALL_MS
      ) {
        // In-flight worker hang only. Queue wait on the shared blur worker
        // must not trip CSS full-frame blur.
        this.hideOverlay();
        this.setBlurActive(false);
      }
      if (this.workerBusy) {
        this.pendingBlob = blob;
        return;
      }
      this.sendFrame(blob);
    }
  }

  dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.pendingBlob = null;
    privacyWorkerClient.unregister(this.streamId);
    this.hideOverlay();
    this.setBlurActive(false);
    this.onFrameSize = null;
    this.onBlurActiveChange = null;
    this.onRoiConfirmedChange = null;
  }

  forceFailForChaos(_reason: string): void {
    this.hideOverlay();
    this.setBlurActive(false);
  }

  private sendFrame(blob: Blob): void {
    this.workerBusy = true;
    this.lastSentAt = Date.now();
    this.latestFrameId += 1;
    privacyWorkerClient.enqueueFrame(this.streamId, this.latestFrameId, blob);
  }

  private flushPendingFrame(): void {
    const blob = this.pendingBlob;
    this.pendingBlob = null;
    if (blob && !this.disposed && !this.peek) {
      this.sendFrame(blob);
    }
  }

  private showOverlay(): void {
    if (this.overlayVisible) {
      return;
    }
    this.overlayVisible = true;
    if (this.onOverlayChange) {
      this.onOverlayChange(true);
    }
  }

  private hideOverlay(): void {
    if (!this.overlayVisible) {
      return;
    }
    this.overlayVisible = false;
    if (this.onOverlayChange) {
      this.onOverlayChange(false);
    }
  }
}
